<template>
  <div>
    <!-- Trigger button — place this in your navbar -->
    <v-btn icon @click="open" class="rn-trigger">
      <v-badge
        :content="unreadCount"
        :value="unreadCount > 0"
        color="#E31B23"
        overlap
      >
        <v-icon color="#6b7280">mdi-bell-outline</v-icon>
      </v-badge>
    </v-btn>

    <!-- Drawer -->
    <v-navigation-drawer
      v-model="drawer"
      right
      fixed
      temporary
      width="420"
      class="rn-drawer"
    >
      <!-- Header -->
      <div class="rn-header d-flex align-center justify-space-between px-4 py-3">
        <div>
          <div class="rn-drawer-title">What's New</div>
          <div class="rn-drawer-sub">Release history</div>
        </div>
        <v-btn icon small @click="drawer = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
      <v-divider />

      <!-- Notes list -->
      <div class="rn-list" ref="listEl">

        <!-- Loading skeleton on first load -->
        <div v-if="loading && notes.length === 0" class="pa-4">
          <v-skeleton-loader v-for="i in 3" :key="i" type="article" class="mb-3" />
        </div>

        <!-- Notes -->
        <div
          v-for="note in notes"
          :key="note.id"
          class="rn-card pa-4"
          :class="{ 'rn-card--unread': isUnread(note.id) }"
        >
          <!-- Version + type + date row -->
          <div class="d-flex align-center flex-wrap mb-1" style="gap:6px">
            <v-chip x-small label color="#ede9fe" text-color="#6d28d9" class="font-weight-bold">
              v{{ note.version }}
            </v-chip>
            <v-chip x-small label :color="typeColor(note.type).bg" :text-color="typeColor(note.type).text">
              {{ note.type }}
            </v-chip>
            <span class="caption grey--text ml-auto">{{ formatDate(note.published_at) }}</span>
          </div>

          <!-- Title -->
          <div class="rn-note-title mb-1">{{ note.title }}</div>

          <!-- Modules -->
          <div v-if="note.modules && note.modules.length" class="d-flex flex-wrap mb-2" style="gap:4px">
            <v-chip
              v-for="mod in note.modules" :key="mod"
              x-small outlined color="#9ca3af"
            >{{ mod }}</v-chip>
          </div>

          <!-- Content — render each line as a bullet -->
          <div class="rn-content">
            <div
              v-for="(line, i) in parseContent(note.content)"
              :key="i"
              class="rn-bullet"
            >
              {{ line }}
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="!loading && notes.length === 0" class="pa-6 text-center">
          <v-icon large color="#d1d5db">mdi-bell-off-outline</v-icon>
          <div class="caption grey--text mt-2">No release notes yet</div>
        </div>

        <!-- Load More -->
        <div v-if="hasMore" class="pa-4 text-center">
          <v-btn
            outlined small color="#6366f1"
            :loading="loadingMore"
            @click="loadMore"
          >
            Load more
          </v-btn>
        </div>

        <!-- End of list -->
        <div v-if="!hasMore && notes.length > 0" class="pa-3 text-center caption grey--text">
          You've seen all release notes
        </div>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
import { public_api } from '@/services/api'

const LS_KEY = 'rn_last_seen_id'
const PER_PAGE = 50

export default {
  name: 'ReleaseNotesDrawer',

  data() {
    return {
      drawer:      false,
      loading:     false,
      loadingMore: false,
      notes:       [],
      page:        1,
      totalPages:  1,
      total:       0,
      unreadCount: 0,
      lastSeenId:  parseInt(localStorage.getItem(LS_KEY) || '0', 10),
    }
  },

  computed: {
    hasMore() { return this.page < this.totalPages },
  },

  mounted() {
    // Fetch first page silently to compute unread badge
    this.fetchNotes(1, true)
  },

  methods: {
    async open() {
      this.drawer = true
      if (this.notes.length === 0) await this.fetchNotes(1)
      // Mark all currently loaded notes as seen
      this.markSeen()
    },

    async fetchNotes(page = 1, silent = false) {
      if (!silent) {
        page === 1 ? (this.loading = true) : (this.loadingMore = true)
      }
      try {
        const { data } = await public_api.get('/release_notes', {
          params: { page, per_page: PER_PAGE }
        })
        if (page === 1) {
          this.notes = data.notes || []
        } else {
          this.notes = [...this.notes, ...(data.notes || [])]
        }
        this.page       = page
        this.totalPages = data.total_pages || 1
        this.total      = data.total       || 0

        // Compute unread from first page (newest notes)
        if (page === 1) {
          this.unreadCount = this.notes.filter(n => n.id > this.lastSeenId).length
        }
      } catch (e) {
        console.error('[ReleaseNotes] fetch error', e)
      } finally {
        this.loading     = false
        this.loadingMore = false
      }
    },

    async loadMore() {
      await this.fetchNotes(this.page + 1)
      this.markSeen()
    },

    markSeen() {
      if (!this.notes.length) return
      const maxId = Math.max(...this.notes.map(n => n.id))
      if (maxId > this.lastSeenId) {
        this.lastSeenId = maxId
        localStorage.setItem(LS_KEY, maxId)
        this.unreadCount = 0
      }
    },

    isUnread(id) { return id > this.lastSeenId },

    parseContent(content) {
      if (!content) return []
      return content
        .split('\n')
        .map(l => l.trim())
        .filter(l => l.length > 0)
        // normalise: add bullet if line doesn't start with one
        .map(l => l.startsWith('•') || l.startsWith('-') ? l : `• ${l}`)
    },

    typeColor(type) {
      const map = {
        feature:     { bg: '#dbeafe', text: '#1e40af' },
        fix:         { bg: '#fef3c7', text: '#92400e' },
        improvement: { bg: '#dcfce7', text: '#166534' },
      }
      return map[type] || { bg: '#f3f4f6', text: '#374151' }
    },

    formatDate(val) {
      if (!val) return ''
      return new Date(val).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric'
      })
    },
  },
}
</script>

<style scoped>
.rn-trigger { position: relative; }

.rn-drawer { box-shadow: -4px 0 20px rgba(0,0,0,0.08) !important; }

.rn-header        { background: #fff; border-bottom: 1px solid #f3f4f6; }
.rn-drawer-title  { font-size: 1rem; font-weight: 700; color: #1a1a2e; }
.rn-drawer-sub    { font-size: 0.75rem; color: #9ca3af; }

.rn-list { height: calc(100vh - 65px); overflow-y: auto; background: #f8f9fb; }

.rn-card {
  background: #fff;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;
}
.rn-card--unread { border-left: 3px solid #6366f1; }

.rn-note-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.4;
}

.rn-content   { margin-top: 6px; }
.rn-bullet    { font-size: 0.8rem; color: #4b5563; line-height: 1.7; padding-left: 2px; }
</style>
